import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLang } from "@/LangContext";
import { scrollToId } from "@/lib/scroll";

const HERO_BG = "/assets/hero-campo.jpg";

export default function Hero() {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-[-12%]">
        <img
          src={HERO_BG}
          alt="Estadio de alto rendimiento"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0C10]/95 via-[#0A0C10]/70 to-[#0A0C10]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-transparent to-[#0A0C10]/70" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-44 sm:px-6 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 flex flex-wrap items-center gap-4"
        >
          <span className="pulse-gold h-2 w-2 rotate-45 bg-amber-400" />
          <span className="font-code text-xs uppercase tracking-[0.4em] text-amber-400">
            {t.hero.eyebrow}
          </span>
          <span className="hidden h-px w-16 bg-white/20 sm:block" />
          <span className="hidden font-code text-xs uppercase tracking-[0.3em] text-slate-400 sm:block">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="max-w-5xl font-display text-5xl font-bold uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-8xl">
          {t.hero.lines.map((line, i) => (
            <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${
                  i === t.hero.lines.length - 1 ? "text-amber-400" : "text-white"
                }`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.95,
                  delay: 0.3 + i * 0.13,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-naming-cta"
            onClick={() => scrollToId("naming")}
            className="group flex items-center gap-3 bg-amber-400 px-7 py-4 font-display text-base font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300"
          >
            {t.hero.ctaPrimary}
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1.5"
            />
          </button>
          <button
            data-testid="hero-explore-cta"
            onClick={() => scrollToId("ecosistema")}
            className="border border-white/25 px-7 py-4 font-display text-base font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-amber-400 hover:text-amber-400"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("ecosistema")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 text-slate-400 transition-colors hover:text-amber-400 md:flex"
      >
        <span className="font-code text-[10px] uppercase tracking-[0.35em]">
          {t.hero.scroll}
        </span>
        <ChevronDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
